import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { RMAMotivo } from '../types';
import { useAuth } from './AuthContext';

/** Lunghezza massima delle note di un pezzo (``RMAUnitIn.descrizione`` lato backend). */
export const RMA_NOTE_MAX = 2000;

export interface RMACartUnit {
  id: string;                  // unique id (uuid) of the physical piece
  motivo: RMAMotivo | '';
  descrizione: string;         // note del Cliente sul pezzo (INT-128)
  photoPath?: string;          // chiave dell'allegato nel bucket
  photoUrl?: string;           // URL presigned per l'anteprima (scade in 12 ore)
  photoName?: string;
  photoMime?: string;          // 'image/jpeg' | 'image/png' | 'application/pdf'
}

export interface RMACartItem {
  // identificazione prodotto
  sku: string;
  title: string;
  brand: string;
  category: string;
  dtkCodice?: string;
  codiceOem?: string;
  // riferimento documento d'origine (D.D.T.)
  orderId: string;
  orderDate: string;
  orderStatus: string;
  docTipork?: string;
  docAnno?: number;
  docSerie?: string;
  docNumdoc?: number;
  // quantita acquistate
  maxQty: number;              // qty max restituibile (già dedotta la qtà resa in precedenza)
  qtyPurchased: number;
  unitPrice: number;
  // pezzi da rendere (un'entry per ciascun pezzo)
  units: RMACartUnit[];
}

interface RMACartContextValue {
  items: RMACartItem[];
  addItem: (item: RMACartItem) => void;
  updateItem: (orderId: string, sku: string, patch: Partial<RMACartItem>) => void;
  removeItem: (orderId: string, sku: string) => void;
  clearCart: () => void;
  totalUnits: number;
  totalLines: number;
  totalValue: number;
}

const RMACartContext = createContext<RMACartContextValue | null>(null);

// Il carrello appartiene a chi l'ha preparato: la chiave include l'utente e
// l'eventuale impersonazione, così un altro account nello stesso browser non
// lo vede né lo invia (audit RMA #19). La vecchia chiave condivisa si scarta.
const STORAGE_PREFIX = 'dtk_rma_cart';
const LEGACY_KEY = 'dtk_rma_cart';

function storageKey(userId?: string, impersonatoDa?: string | null): string | null {
  if (!userId) return null;
  return `${STORAGE_PREFIX}:${userId}:${impersonatoDa || ''}`;
}

function load(key: string | null): RMACartItem[] {
  if (!key) return [];
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
}

export function RMACartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const key = storageKey(user?.userId, user?.impersonatoDa);
  const [state, setState] = useState<{ key: string | null; items: RMACartItem[] }>(() => ({ key, items: load(key) }));
  // Al cambio di utente si ricarica il carrello di quell'utente, prima di
  // qualunque scrittura: niente travaso da un account all'altro.
  const items = state.key === key ? state.items : load(key);
  if (state.key !== key) setState({ key, items });

  const setItems = useCallback((fn: (prev: RMACartItem[]) => RMACartItem[]) => {
    setState(prev => ({ ...prev, items: fn(prev.items) }));
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_KEY);
      if (state.key) localStorage.setItem(state.key, JSON.stringify(state.items));
    } catch { /* storage non disponibile: il carrello resta in memoria */ }
  }, [state]);

  const addItem = useCallback((item: RMACartItem) => {
    setItems(prev => {
      // Se già esiste lo stesso prodotto dallo stesso ordine, fondiamo le units
      const idx = prev.findIndex(i => i.orderId === item.orderId && i.sku === item.sku);
      if (idx >= 0) {
        const merged = [...prev];
        const newUnits = [...merged[idx].units, ...item.units];
        // Cap su maxQty
        const capped = newUnits.slice(0, merged[idx].maxQty);
        merged[idx] = { ...merged[idx], units: capped };
        return merged;
      }
      return [...prev, item];
    });
  }, [setItems]);

  const updateItem = useCallback((orderId: string, sku: string, patch: Partial<RMACartItem>) => {
    setItems(prev => prev.map(i =>
      i.orderId === orderId && i.sku === sku ? { ...i, ...patch } : i
    ));
  }, [setItems]);

  const removeItem = useCallback((orderId: string, sku: string) => {
    setItems(prev => prev.filter(i => !(i.orderId === orderId && i.sku === sku)));
  }, [setItems]);

  const clearCart = useCallback(() => setItems(() => []), [setItems]);

  const totalUnits = items.reduce((s, i) => s + i.units.length, 0);
  const totalLines = items.length;
  const totalValue = items.reduce((s, i) => s + i.unitPrice * i.units.length, 0);

  return (
    <RMACartContext.Provider value={{ items, addItem, updateItem, removeItem, clearCart, totalUnits, totalLines, totalValue }}>
      {children}
    </RMACartContext.Provider>
  );
}

export function useRMACart(): RMACartContextValue {
  const ctx = useContext(RMACartContext);
  if (!ctx) throw new Error('useRMACart must be used within RMACartProvider');
  return ctx;
}
